import { CurrencyContext, LocaleContext } from '@tock/coretypes';
import { IntlProvider } from 'react-intl';
import { SearchHandlers, useSearchHandlers } from 'hooks/search/useSearchHandlers';
import { SearchMode } from 'components/search/utils';
import { isEmptyChildren } from 'utils/isEmptyChildren';
import { isFunction } from 'lodash';
import React, { FunctionComponent, useContext } from 'react';

interface SearchContextProps {
  locale?: string;
  currencyCode?: string;
  type: SearchMode['stateType'];
}

export const SearchHandlerContext = React.createContext<SearchHandlers | undefined>(undefined);

/**
 * exposes the search handlers methods and state
 */
export const useSearchHandlerContext = () => useContext(SearchHandlerContext);

/**
 * Search context sets up an overall currency and locale context for the page, and passes
 * a mode {SearchMode} to the useSearchHandlers hook. All components in this context
 * should use the handlers from the {SearchHandlerContext}.
 * @param {SearchContextProps} searchContextProps
 */
export const SearchContext: FunctionComponent<SearchContextProps> = ({
  currencyCode = 'USD',
  locale = 'en-us',
  type,
  children,
}) => {
  const searchHandlers = useSearchHandlers(type);
  return (
    <IntlProvider locale={locale}>
      <CurrencyContext.Provider value={currencyCode}>
        <LocaleContext.Provider value={locale}>
          <SearchHandlerContext.Provider value={searchHandlers}>
            {children
              ? isFunction(children)
                ? (children as (handlers: SearchHandlers) => React.ReactNode)(
                    searchHandlers as SearchHandlers
                  )
                : !isEmptyChildren(children)
                ? React.Children.only(children)
                : null
              : null}
          </SearchHandlerContext.Provider>
        </LocaleContext.Provider>
      </CurrencyContext.Provider>
    </IntlProvider>
  );
};
